import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import type { TicketAppointmentDto } from "@/api/types";
import { DateRange, formatDateRange } from "@/components/DateRange/DateRange";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { Divider } from "@/design-system/Divider";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useBool } from "@/hooks/useBool";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";

type TicketResidentAppointmentCardProps = {
  ticketId: string;
};

export function TicketResidentAppointmentCard({ ticketId }: TicketResidentAppointmentCardProps): React.ReactNode {
  const { t } = useTranslation();
  const { data: status } = useQuery(useTicketQueries().appointment(ticketId));
  const [isModalOpened, modalHandlers] = useBool(false);
  const [modalMode, setModalMode] = useState<"pick" | "change">("pick");

  // Hide entirely when the project has no integration (the request 404s) or nothing to show yet.
  if (!status || (status.state === "notStarted" && !status.selectedAppointmentStart)) {
    return null;
  }

  const hasAppointment = Boolean(status.selectedAppointmentStart) && Boolean(status.selectedAppointmentEnd);
  const canChange = hasAppointment && status.canChange;
  const canPick = status.canSelect && status.options.length > 0 && !hasAppointment;
  const isPending = status.state === "submitted" || status.state === "fetchingAppointments";
  const needsFollowUp = status.state === "manualFollowUp" || status.state === "failed" || status.requiresManualFollowUp;

  const openModal = (mode: "pick" | "change") => {
    setModalMode(mode);
    modalHandlers.setTrue();
  };

  let noMomentSubtitle: string;
  if (isPending) {
    noMomentSubtitle = t("page.tickets.appointment.resident.pending");
  } else if (canPick) {
    noMomentSubtitle = t("page.tickets.appointment.resident.no-moment.pick-hint");
  } else {
    noMomentSubtitle = t("page.tickets.appointment.resident.no-moment.follow-up");
  }

  return (
    <Stack gap="md">
      <Divider />
      {hasAppointment && (
        <Stack gap="sm">
          <Stack gap="xs">
            <Text variant="overline-emphasis" tone="secondary" overflow="normal">
              {t("page.tickets.appointment.resident.expected-arrival")}
            </Text>
            <Text as="div" variant="body-emphasis" overflow="normal" data-testid="jh-resident-appointment">
              <DateRange
                start={status.selectedAppointmentStart!}
                end={status.selectedAppointmentEnd!}
                format="shortWithTime"
              />
            </Text>
            <Text variant="caption" tone="secondary" overflow="normal">
              {t("page.tickets.appointment.resident.arrival-note")}
            </Text>
          </Stack>
          {canChange && (
            <Inline wrap={false}>
              <Button
                variant="ghostPrimary"
                size="sm"
                data-testid="jh-resident-change-moment"
                onClick={() => openModal("change")}
              >
                {t("page.tickets.appointment.resident.change-moment")}
              </Button>
            </Inline>
          )}
        </Stack>
      )}

      {!hasAppointment && (canPick || needsFollowUp || isPending) && (
        <Stack gap="sm">
          <Stack gap="xs">
            <Text variant="body-emphasis" overflow="normal">
              {t("page.tickets.appointment.resident.no-moment.title")}
            </Text>
            <Text variant="caption" tone="secondary" overflow="normal">
              {noMomentSubtitle}
            </Text>
          </Stack>
          {canPick && (
            <Inline wrap={false}>
              <Button
                variant="soft"
                size="sm"
                data-testid="jh-resident-choose-moment"
                onClick={() => openModal("pick")}
              >
                {t("page.tickets.appointment.resident.choose-moment")}
              </Button>
            </Inline>
          )}
        </Stack>
      )}

      <ResidentAppointmentModal
        ticketId={ticketId}
        mode={modalMode}
        status={status}
        isOpened={isModalOpened}
        onOpenChange={modalHandlers.set}
      />
    </Stack>
  );
}

type ResidentAppointmentModalProps = ModalBaseProps & {
  ticketId: string;
  mode: "pick" | "change";
  status: TicketAppointmentDto;
};

function ResidentAppointmentModal({
  ticketId,
  mode,
  status,
  isOpened,
  onOpenChange,
}: ResidentAppointmentModalProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const [selectedSlotId, setSelectedSlotId] = useState<string>();
  const hasRequestedChangeRef = useRef(false);

  const selectAppointmentMutation = ticketMutations.useSelectAppointment(ticketId);
  const changeAppointmentMutation = ticketMutations.useChangeAppointment(ticketId);

  const isChangeMode = mode === "change";
  // In change mode the fresh candidate moments come from the change call; in pick mode they are the
  // ones already attached to the ticket (the resident skipped and is choosing now).
  const options = isChangeMode ? (changeAppointmentMutation.data?.data.options ?? []) : status.options;
  const currentAppointment =
    status.selectedAppointmentStart && status.selectedAppointmentEnd
      ? { start: status.selectedAppointmentStart, end: status.selectedAppointmentEnd }
      : undefined;

  // Ask the repair provider for new candidate moments once, when the change modal opens.
  useEffect(() => {
    if (isOpened && isChangeMode && !hasRequestedChangeRef.current) {
      hasRequestedChangeRef.current = true;
      changeAppointmentMutation.mutate();
    }
  }, [isOpened, isChangeMode, changeAppointmentMutation]);

  const handleOpenChange = (state: boolean) => {
    if (!state) {
      setSelectedSlotId(undefined);
      hasRequestedChangeRef.current = false;
      changeAppointmentMutation.reset();
    }

    onOpenChange(state);
  };

  const handleConfirm = () => {
    if (!selectedSlotId) {
      return;
    }

    selectAppointmentMutation.mutate(selectedSlotId, {
      onSuccess: (response) => {
        if (response.data.state === "confirmed" || response.data.state === "appointmentChanged") {
          handleOpenChange(false);

          return;
        }

        // The slot was taken between listing and confirming: drop the dead selection. In change
        // mode the shown options come from the change call, so re-request fresh ones; in pick mode
        // they re-read from the status query the mutation already invalidated.
        setSelectedSlotId(undefined);
        if (isChangeMode) {
          changeAppointmentMutation.mutate();
        }
      },
    });
  };

  // In change mode, keep the loader up from open until the fresh moments (or an error) arrive.
  const isLoadingOptions = isChangeMode && !changeAppointmentMutation.data && !changeAppointmentMutation.isError;

  return (
    <Modal.Root
      title={t(
        isChangeMode
          ? "page.tickets.appointment.resident.change.title"
          : "page.tickets.appointment.resident.pick.title",
      )}
      description={isChangeMode ? undefined : t("page.tickets.appointment.resident.pick.subtitle")}
      isOpened={isOpened}
      onOpenChange={handleOpenChange}
      height={560}
    >
      <Modal.Body>
        {isLoadingOptions ? (
          <FullSizeLoader size="sm" noDelay />
        ) : (
          <Stack gap="md">
            {isChangeMode && currentAppointment && (
              <Notice
                variant="warning"
                message={t("page.tickets.appointment.resident.change.current", {
                  moment: formatDateRange(i18n, { ...currentAppointment, format: "shortWithTime" }),
                })}
              />
            )}
            {isChangeMode && (
              <Text variant="body-emphasis" overflow="normal">
                {t("page.tickets.appointment.resident.change.choose-new")}
              </Text>
            )}
            {options.length === 0 ? (
              <Text variant="caption" tone="secondary" overflow="normal" data-testid="jh-resident-no-options">
                {t("page.tickets.appointment.resident.change.no-options")}
              </Text>
            ) : (
              <Stack gap="sm">
                {options.map((option) => (
                  <RadioCard
                    key={option.slotId}
                    name="jh-resident-moment"
                    value={option.slotId}
                    checked={option.slotId === selectedSlotId}
                    onChange={() => setSelectedSlotId(option.slotId)}
                    label={formatDateRange(i18n, { start: option.start, end: option.end, format: "shortWithTime" })}
                    data-testid="jh-resident-moment-option"
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Modal.Body>
      <Modal.Actions isSticky>
        <Button
          data-testid="jh-resident-confirm-btn"
          disabled={!selectedSlotId}
          isLoading={selectAppointmentMutation.isPending}
          onClick={handleConfirm}
        >
          {t(
            isChangeMode
              ? "page.tickets.appointment.resident.change.confirm"
              : "page.tickets.appointment.resident.pick.confirm",
          )}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
